import api from "./api";

// Club directory (admin panel). GET /api/clubs and GET /api/clubs/{id} carry
// no auth/permission middleware on the backend — the "view_clubs" gate on the
// admin nav/route is frontend-only, matching the same pattern already used
// for Facilities.
//
// Note: there are two separate club *update* routes. The singular
// PUT/PATCH /api/club/{id} is gated by "primary.coach" middleware, which
// requires the caller to be authenticated as a MemberRegistration (the
// club's own coach) — an admin bearer token can never satisfy it. The plural
// PUT/PATCH /api/clubs/{id} (see updateClub() below) is the admin-usable one:
// gated only by "edit_clubs", seeded onto admin/Director Physical
// Education/Instructor.

// GET /api/clubs — optional { search } filter. Returns a plain array of
// clubs: {id, club_code, club_name, reg_year, reg_no, membership_start_date,
// membership_end_date, club_status_id, club_status, primary_phone_number,
// secondary_phone_number, address, no_of_coaches, approved_member_count,
// pending_member_request_count}.
export const listClubs = (params = {}) =>
  api.get("/api/clubs", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/clubs/{id} — flat object with the same fields as the list row plus
// `recent_members` (last 10 members linked/requested to this club).
export const getClub = (id) => api.get(`/api/clubs/${id}`).then((r) => r.data);

// GET /api/clubs/expired — admin listing, gated by "permission:view_clubs"
// (unlike the two routes above, this one DOES carry real backend auth/
// permission middleware — auth:sanctum + view_clubs — so it needs a system
// user token; a coach/member token can't reach it). Matches clubs whose
// club_status is "Expired" OR whose membership_end_date has already passed.
// Optional { search } filter (matches club name/reg_no/primary coach name or
// email). Response: { as_of, count, clubs: [...] } — same row shape as
// listClubs plus registered_by_member_id/registered_by_member_code and
// primary_coach_email/initials/lastname (fields listClubs/getClub don't
// carry) — not a plain array, so callers read `.clubs`.
export const listExpiredClubs = (params = {}) =>
  api
    .get("/api/clubs/expired", { params })
    .then((r) => ({ as_of: r.data?.as_of ?? null, count: r.data?.count ?? 0, clubs: Array.isArray(r.data?.clubs) ? r.data.clubs : [] }));

// PATCH /api/clubs/{id} — admin/staff edit, gated by "edit_clubs". Every
// field is optional server-side (ClubController::update validates them all
// as "sometimes") — PUT and PATCH hit the exact same controller method with
// the same validation, so there's no real "full vs. partial" distinction;
// pass just the fields you want to change. Accepts { club_name, reg_year,
// reg_no, membership_start_date, membership_end_date, primary_phone_number,
// secondary_phone_number, address }. reg_no is validated unique against
// other clubs. Returns { message, ...updated club fields } (same shape as
// getClub()).
export const updateClub = (id, payload) => api.patch(`/api/clubs/${id}`, payload).then((r) => r.data);

// POST /api/clubs/{id}/inactivate — admin-only (permission:deactivate_clubs,
// seeded onto admin/Director Physical Education/Instructor). Only a club
// currently "Active" can be inactivated (422 otherwise); also revokes portal
// session tokens for every coach on the club server-side. Optional `reason`
// (free text, stored for audit, nullable). Returns { message, club } — the
// freshly reloaded club record (same shape as getClub()).
export const inactivateClub = (id, reason) =>
  api.post(`/api/clubs/${id}/inactivate`, reason ? { reason } : {}).then((r) => r.data);

// POST /api/clubs/{id}/reactivate — same permission as inactivate above.
// Only a club currently "Inactive" can be reactivated (422 otherwise).
// Returns { message, club }.
export const reactivateClub = (id) =>
  api.post(`/api/clubs/${id}/reactivate`).then((r) => r.data);

// POST /api/staff/club-registrations — staff-desk registration: a system
// user starts a PENDING club_registration_process (permission:
// create_staff_club_registrations) on a walk-in club's behalf, with no coach
// OTP gate. Same multipart payload shape and validation as the public
// ClubRegistration.jsx flow (club_name, reg_no, primary_phone_number,
// secondary_phone_number, address, coaches (JSON array of {title, initials,
// name_denoted_by_initials, lastname, member_gender_id, email, nic,
// primary_phone, secondary_phone, date_of_birth, address} — every coach,
// including the first/primary one, supplies their own email directly since
// there's no OTP-verified session to inherit it from), coach_photos[i] (file,
// required for coach 0, optional for the rest) — just without requiring a
// verified responsible-coach email first. Returns { process_id,
// payable_amount, payment_reference, ... } like the public flow; the club is
// only finalized once payment clears (shroff voucher + the existing "Confirm
// Payment" tab's manual-confirm, the same submit/approve queue public
// counter-payments already use) — unlike createClubLegacy(), which finalizes
// in one step together with the payment record.
export const createStaffClubRegistration = (formData) =>
  api.post("/api/staff/club-registrations", formData).then((r) => {
    let data = r.data;
    while (Array.isArray(data)) data = data[0];
    return data;
  });

// POST /api/clubs/legacy — admin-created ("legacy") club, used by the
// back-office Members page's "Add Member" flow when the operator picks
// "Club" instead of a member type: registers a brand-new club together with
// its coaches in one call (no OTP, unlike the public /club-registration
// flow). Multipart FormData. Fields: club_name, reg_no, primary_phone_number,
// secondary_phone_number, address, membership_start_date,
// membership_end_date, coaches (JSON-stringified array of {title, initials,
// nameWithInitials, lastName, memberGenderId, email, nic, primaryPhone,
// secondaryPhone, dob, address, coach_role}) — every key here is camelCase
// and gets normalized server-side EXCEPT coach_role, which must be sent as
// this literal snake_case key (confirmed by a 422 on "coaches.{i}.coach_role"
// when it was sent as "coachRole"). Exactly one coach must have coach_role
// "primary", the rest "other" — plus coach_photos[i] (file, one per
// coach, same index as its entry in the coaches array; same convention as
// the public /api/club-registrations flow's coach_photos[]). Only the
// primary coach's photo (coach_photos[0]) is required; other coaches' are
// optional, matching the public flow's rule.
export const createClubLegacy = (formData) =>
  api.post("/api/clubs/legacy", formData).then((r) => (Array.isArray(r.data) ? r.data[0] : r.data));
